import { AlertTriangle, RefreshCw, Siren } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { acknowledgeEscalationRun, listEscalationRuns, type EscalationRunRow } from '../api';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';

const POLL_INTERVAL_MS = 15000;

function toneForRun(run: EscalationRunRow): 'success' | 'warn' | 'danger' | 'neutral' {
  if (run.status === 'resolved') return 'success';
  if (run.status === 'acknowledged') return 'neutral';
  if (run.status === 'exhausted') return 'danger';
  return 'warn';
}

function EscalationRunsPage() {
  const { session } = useSession();
  const { activeOrganization } = useWorkspace();
  const { toast } = useFeedback();

  const [runs, setRuns] = useState<EscalationRunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acknowledgingId, setAcknowledgingId] = useState('');
  const hasLoadedOnce = useRef(false);

  const load = useCallback(async () => {
    if (!session || !activeOrganization) return;
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const rows = await listEscalationRuns(session.accessToken, {
        organizationId: activeOrganization.id
      });
      setRuns(rows);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load escalation runs.');
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  }, [session, activeOrganization]);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => { void load(); }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [load]);

  async function handleAcknowledge(run: EscalationRunRow) {
    if (!session) return;
    setAcknowledgingId(run.escalationRunId);
    try {
      const updated = await acknowledgeEscalationRun(session.accessToken, run.escalationRunId);
      setRuns((previous) => previous.map((r) => (r.escalationRunId === updated.escalationRunId ? updated : r)));
      toast('success', `Acknowledged "${run.title}"`);
    } catch (err: unknown) {
      toast('error', err instanceof Error ? err.message : 'Failed to acknowledge escalation run.');
    } finally {
      setAcknowledgingId('');
    }
  }

  const sortedRuns = [...runs].sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));

  return (
    <SectionCard
      label="On-call"
      title="Escalation runs"
      description="Every alert that entered an escalation policy, most recent first. Refreshes automatically every 15 seconds."
      action={
        <button type="button" className="panelControl" onClick={() => void load()} disabled={loading}>
          <RefreshCw size={14} />
          <span>{loading ? 'Loading…' : 'Refresh'}</span>
        </button>
      }
    >
      {error ? (
        <EmptyState icon={<AlertTriangle size={20} />} title="Could not load escalation runs" description={error} />
      ) : loading && sortedRuns.length === 0 ? (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
          Loading escalation runs…
        </div>
      ) : sortedRuns.length === 0 ? (
        <EmptyState
          icon={<Siren size={20} />}
          title="No escalation runs yet"
          description="Runs appear here once an alert source dispatches an alert into an escalation policy."
        />
      ) : (
        <div className="releaseTableWrap" role="region" aria-label="Escalation runs table" tabIndex={0}>
          <table className="releaseTable">
            <thead>
              <tr>
                <th>Title</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Started</th>
                <th>Acknowledged</th>
                <th>Resolved</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sortedRuns.map((run) => (
                <tr key={run.escalationRunId}>
                  <td>
                    <div className="releaseIdentity">
                      <strong>{run.title}</strong>
                      {run.description ? <span>{run.description}</span> : null}
                    </div>
                  </td>
                  <td>{run.severity}</td>
                  <td>
                    <StatusChip tone={toneForRun(run)}>{run.status}</StatusChip>
                  </td>
                  <td>
                    <div className="releaseIdentity">
                      <strong>{formatRelativeDate(run.startedAt)}</strong>
                      <span>{formatAbsoluteDate(run.startedAt)}</span>
                    </div>
                  </td>
                  <td>{run.acknowledgedAt ? formatRelativeDate(run.acknowledgedAt) : '—'}</td>
                  <td>{run.resolvedAt ? formatRelativeDate(run.resolvedAt) : '—'}</td>
                  <td className="releaseTableAction">
                    {run.status === 'pending' ? (
                      <button
                        type="button"
                        className="secondaryButton"
                        disabled={acknowledgingId === run.escalationRunId}
                        onClick={() => handleAcknowledge(run)}
                      >
                        {acknowledgingId === run.escalationRunId ? 'Acknowledging…' : 'Acknowledge'}
                      </button>
                    ) : (
                      <span className="recoveryUnavailable">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export { EscalationRunsPage };
